import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import {useCallback, useRef, useState} from 'react'

import type {CreateArtifactType} from './create-artifact-dialog-types'
import {CreateArtifactDialog} from './CreateArtifactDialog'
import {CreateLinkDialog} from './CreateLinkDialog'

type UseCreateArtifactDialogArgs = {
  resourceId?: number | null
  onCreated?: () => void
}

export function useCreateArtifactDialog({resourceId, onCreated = noop}: UseCreateArtifactDialogArgs) {
  const [creationType, setCreationType] = useState<CreateArtifactType | null>(null)
  const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const resourceIdIsReady = typeof resourceId === 'number' && resourceId > 0
  const htmlArtifactsEnabled = isFeatureEnabled('scribe_artifacts_html')

  const openCreateArtifactDialog = useCallback(() => {
    if (!resourceIdIsReady) return
    setCreationType('generic')
  }, [resourceIdIsReady])

  const openCreateHTMLArtifactDialog = useCallback(() => {
    if (!resourceIdIsReady || !htmlArtifactsEnabled) return
    setCreationType('embedded_html')
  }, [htmlArtifactsEnabled, resourceIdIsReady])

  const openCreateLinkDialog = useCallback(() => {
    if (!resourceIdIsReady) return
    setCreateLinkDialogOpen(true)
  }, [resourceIdIsReady])

  const closeCreateArtifactDialog = useCallback(() => {
    setCreationType(null)
    setCreateLinkDialogOpen(false)
  }, [])

  const createArtifactDialog =
    creationType && resourceIdIsReady && (creationType !== 'embedded_html' || htmlArtifactsEnabled) ? (
      <CreateArtifactDialog
        key={creationType}
        resourceId={resourceId}
        creationType={creationType}
        onClose={closeCreateArtifactDialog}
        onCreated={onCreated}
        returnFocusRef={triggerRef}
      />
    ) : createLinkDialogOpen && resourceIdIsReady ? (
      <CreateLinkDialog
        resourceId={resourceId}
        onClose={closeCreateArtifactDialog}
        onCreated={onCreated}
        returnFocusRef={triggerRef}
      />
    ) : null

  return {
    createArtifactDialog,
    openCreateArtifactDialog,
    openCreateHTMLArtifactDialog,
    openCreateLinkDialog,
    resourceIdIsReady,
    triggerRef,
  }
}
